// src/components/WhatsAppTemplates/WhatsAppTemplates.js
import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import { getAccessJwtToken } from "../../store/globalUtilities";
import CmsContext from "../../store/cms-context";
import cloneDeep from "lodash/cloneDeep";
import WhatsAppTemplatesTable from "../WhatsAppTemplatesTable/WhatsAppTemplatesTable";
import WhatsAppTemplatesDialog from "../WhatsAppTemplatesDialog/WhatsAppTemplatesDialog";
import WhatsAppTestDialog from "../WhatsAppTestDialog/WhatsAppTestDialog";
import { default_template } from "../../Utilities/Metrics";

const TEMPLATES_API =
  "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/whatsapp/templates";

const CAMPAIGNS_API =
  "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/whatsapp/campaigns";

// Names that can be edited but NOT deleted
const PROTECTED_TEMPLATE_NAMES = new Set([
  "onboarding_welcome_flow",
  "utility_optout",
]);

function normalizeName(t) {
  return (
    (t?.name ?? t?.template_name ?? t?.template?.name ?? "")
      .toString()
      .trim()
      .toLowerCase()
  );
}

export default React.memo(function WhatsAppTemplates() {
  const cmsContext = useContext(CmsContext);
  const isMountedRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]);
  const [dialogState, setDialogState] = useState(cloneDeep(default_template));

  // Set of existing template names (normalized, case-insensitive)
  const usedTemplateNameSet = useMemo(() => {
    return new Set(
      (allTemplates || [])
        .map((t) =>
          (t?.template_name ?? "")
            .toString()
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    );
  }, [allTemplates]);

  const [testDialog, setTestDialog] = useState({
    isOpen: false,
    template: null,
  });

  // Expose to children to “grey out” delete actions
  const isTemplateProtected = (t) =>
    PROTECTED_TEMPLATE_NAMES.has(normalizeName(t));

  useEffect(() => {
    isMountedRef.current = true;
    fetchAllTemplates();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  async function fetchAllTemplates() {
    setIsLoading(true);
    try {
      const auth = await getAccessJwtToken();
      const res = await fetch(TEMPLATES_API, {
        method: "GET",
        headers: {
          Authorization: auth.jwt,
          email: cmsContext.current_shop.email,
        },
      });
      const data = await res.json();
      if (!isMountedRef.current) return;

      if (Array.isArray(data.templates)) {
        setAllTemplates(data.templates);
      } else {
        cmsContext.setSnackBarOpen(true, "error", "Failed to load templates.");
      }
    } catch (err) {
      console.error(err);
      if (isMountedRef.current)
        cmsContext.setSnackBarOpen(true, "error", "Error fetching templates.");
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }

    async function handleSave(template) {
    const getMimeFromName = (name = "") => {
      const ext = name.toLowerCase().split(".").pop();
      if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
      if (ext === "png") return "image/png";
      if (ext === "mp4") return "video/mp4";
      return "";
    };
    
    // Prevent duplicate names (case-insensitive).
    const isEdit = dialogState.type === "edit";
    const incomingNameNorm = (template?.name ?? "")
      .toString()
      .trim()
      .toLowerCase();
    // Allow keeping the same name on the same template when editing.
    const currentEditingId = dialogState?.template?.template_id || dialogState?.template?.id;
    const nameClashWithAnother = (allTemplates || []).some((t) => {
      const tNorm = (t?.template_name ?? "").toString().trim().toLowerCase();
      const tId = t?.template_id || t?.id;
      return tNorm === incomingNameNorm && (!isEdit || tId !== currentEditingId);
    });
    if (incomingNameNorm && nameClashWithAnother) {
      cmsContext.setSnackBarOpen(true, "error", "Template name is already in use.");
      throw new Error("Duplicate template name");
    }
    const auth = await getAccessJwtToken();
    let image_url = template.image_url || "";
    let media_id = template.media_id || "";
    let media_type = template.media_type || "";

    if (template.image instanceof File) {
      // Figure out content type
      const inferred = getMimeFromName(template.image.name);
      const contentType = template.image.type || inferred || "application/octet-stream";
      media_type = contentType.startsWith("video/") ? "video" : "image";

      // Ask backend for a presigned PUT + a GET link WhatsApp can fetch
      //    Response shape: { putUrl, getUrl, s3Key }
      const presignRes = await fetch(`${TEMPLATES_API}/presign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth.jwt,
          email: cmsContext.current_shop.email,
        },
        body: JSON.stringify({
          contentType,                    // e.g. "video/mp4" or "image/jpeg"
          filename: template.image.name,
        }),
      });

      if (!presignRes.ok) {
        console.error(await presignRes.text().catch(() => ""));
        cmsContext.setSnackBarOpen(true, "error", "Could not get upload URL.");
        throw new Error("Presign failed");
      }

      const { putUrl, getUrl, key } = await presignRes.json();

      // Upload file bytes directly to S3 (no API Gateway limit)
      const s3Put = await fetch(putUrl, {
        method: "PUT",
        headers: { "Content-Type": contentType },
        body: template.image,
      });
      if (!s3Put.ok) {
        console.error(await s3Put.text().catch(() => ""));
        cmsContext.setSnackBarOpen(true, "error", "Upload to storage failed.");
        throw new Error("S3 upload failed");
      }

      // Tell Lambda to convert link -> media_id (no big body)
      const putRes = await fetch(TEMPLATES_API, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth.jwt,
          email: cmsContext.current_shop.email,
        },
        body: JSON.stringify({
        s3Key: key,
        contentType,
        }),
      });

      if (!putRes.ok) {
        console.error(await putRes.text().catch(() => ""));
        cmsContext.setSnackBarOpen(true, "error", "Media registration failed.");
        throw new Error("Media registration failed");
      }

      const payload = await putRes.json();
      image_url = getUrl.split("?")[0];                 
      media_id  = payload.media_id;     
    }

    const body = {
      request_type: isEdit ? "edit" : "create",
      id: template.template_id || template.id,
      template_name: template.name,
      campaign_url: template.campaign_url,
      utm_source: template.utm_source,
      utm_campaign: template.utm_campaign,
      first_name_enabled: template.first_name_enabled,
      image_url,
      media_id,
      media_type,
      tested: isEdit ? false : template.tested,
    };

    const postRes = await fetch(TEMPLATES_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: auth.jwt,
        email: cmsContext.current_shop.email,
      },
      body: JSON.stringify(body),
    });
    const data = await postRes.json();
    if (!postRes.ok) {
      console.error(data);
      cmsContext.setSnackBarOpen(true, "error", data.error || "Save failed");
      throw new Error(data.error || "Save failed");
    }
    cmsContext.setSnackBarOpen(true, "success", data.message || "Saved!");
    if (isMountedRef.current) await fetchAllTemplates();
    closeDialog();
  }

async function getCampaignTemplateIdSet() {
  const auth = await getAccessJwtToken();
  const res = await fetch(CAMPAIGNS_API, {
    method: "GET",
    headers: {
      Authorization: auth.jwt,
      email: cmsContext.current_shop.email,
    },
  });
  if (!res.ok) {
    // Fail soft – if we can't fetch campaigns, treat as none to avoid blocking all deletes
    console.error("Failed to fetch campaigns before delete:", await res.text().catch(() => ""));
    return new Set();
  }
  const data = await res.json();
  const campaigns = Array.isArray(data.campaigns) ? data.campaigns : [];
  const ids = new Set();
  for (const c of campaigns) {
    // cover both shapes: { template_id } or { template: { template_id } }
    const tid = c?.template?.template_id ?? c?.template_id;
    if (tid) ids.add(tid);
  }
  return ids;
}

// Keep server-side guard + UI block
async function deleteTemplates(selected) {
  if (!selected || !selected.length) return;

  // Check which templates are referenced by campaigns
  const inUseTemplateIds = await getCampaignTemplateIdSet();

  const protectedChosen = selected.filter(isTemplateProtected);
  const inUseChosen = selected.filter(
    (t) => inUseTemplateIds.has(t.template_id || t.id)
  );

  // Report why some are being skipped
  if (protectedChosen.length) {
    cmsContext.setSnackBarOpen(
      true,
      "warning",
      "‘onboarding_welcome_flow’ and ‘opt_out_template’ cannot be deleted — only edited."
    );
  }
  if (inUseChosen.length) {
    const names = inUseChosen
      .map((t) => t.name || t.template_name || "Unnamed")
      .slice(0, 3) // avoid overly long snackbars
      .join(", ");
    cmsContext.setSnackBarOpen(
      true,
      "warning",
      `Skipped ${inUseChosen.length} template(s) that are used by existing campaign(s)${
        names ? ` (e.g. ${names}${inUseChosen.length > 3 ? ", ..." : ""})` : ""
      }.`
    );
  }

  // Only allow deletions for templates that are neither protected nor in use
  const deletable = selected.filter(
    (t) =>
      !isTemplateProtected(t) && !inUseTemplateIds.has(t.template_id || t.id)
  );
  if (!deletable.length) return;

  // Confirm with the user
  const confirmDelete = window.confirm(
    `Are you sure you want to delete ${deletable.length} template(s)?`
  );
  if (!confirmDelete) return;

  // Proceed with deletion
  const auth = await getAccessJwtToken();
  const res = await fetch(TEMPLATES_API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: auth.jwt,
      email: cmsContext.current_shop.email,
    },
    body: JSON.stringify({
      request_type: "delete",
      templates: deletable.map((t) => ({ id: t.template_id || t.id })),
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    console.error(data);
    cmsContext.setSnackBarOpen(true, "error", data.error || "Delete failed");
    return;
  }
  cmsContext.setSnackBarOpen(true, "success", data.message || "Deleted!");
  if (isMountedRef.current) await fetchAllTemplates();
}

  function openCreateDialog() {
    const dt = cloneDeep(default_template);
    dt.is_dialog_open = true;
    dt.type = "create";
    setDialogState(dt);
  }
  function openEditDialog(template) {
    const dt = cloneDeep(default_template);
    dt.is_dialog_open = true;
    dt.type = "edit";
    dt.template = template;
    setDialogState(dt);
  }
  function closeDialog() {
    setDialogState((d) => ({ ...d, is_dialog_open: false }));
  }

  function openTestDialog(template) {
    setTestDialog({ isOpen: true, template });
  }
  function closeTestDialog() {
    setTestDialog({ isOpen: false, template: null });
  }

  async function handleSendTest(recipients) {
    const auth = await getAccessJwtToken();
    const res = await fetch(TEMPLATES_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: auth.jwt,
        email: cmsContext.current_shop.email,
      },
      body: JSON.stringify({
        request_type: "test",
        id: testDialog.template.template_id,
        recipients,
      }),
    });
    if (!res.ok) {
      let err = res.statusText;
      try {
        const j = await res.json();
        err = j.error || JSON.stringify(j);
      } catch {}
      throw new Error(`Test API ${res.status}: ${err}`);
    }
    const { results } = await res.json();
    cmsContext.setSnackBarOpen(true, "success", "Test sent!");
    return results;
  }

  async function handleMarkSuccess() {
    const auth = await getAccessJwtToken();
    const res = await fetch(TEMPLATES_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: auth.jwt,
        email: cmsContext.current_shop.email,
      },
      body: JSON.stringify({
        request_type: "edit",
        id: testDialog.template.template_id,
        tested: true,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      cmsContext.setSnackBarOpen(
        true,
        "error",
        data.error || "Mark tested failed"
      );
      throw new Error(data.error || "Mark tested failed");
    }
    cmsContext.setSnackBarOpen(true, "success", "Template marked as tested!");
    if (isMountedRef.current) await fetchAllTemplates();
    closeTestDialog();
  }

  return (
    <>
      {isLoading ? (
        <CircularProgress
          sx={{ position: "absolute", top: "50%", left: "50%" }}
        />
      ) : (
        <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
          <Container maxWidth="lg">
            <Stack direction="column" spacing={2} mb={3}>
              <Typography variant="h5">WhatsApp Message Templates</Typography>
              <Typography variant="body1" color="textSecondary">
                Use <strong>“New Template”</strong> to select from approved templates in our WhatsApp Business account.
                You can then configure the <strong>Campaign URL</strong> (if applicable), set UTM terms and upload a <strong>.jpg/.png</strong> image (maxmium 5MB) or a <strong>.mp4</strong> video (maximum 16MB).
                <br/><br/>
                <strong>Required fields:</strong> Template Name, UTM Terms, whether the template has a first name parameter and a media file (.jpg/.png/.mp4).
                <strong> Optional field:</strong> Campaign URL (not always required, e.g., onboarding messages).
                <br/><br/>
                When a template is saved, its “Tested” status will be set to <em>Untested</em>. Any edit will reset it to <em>Untested</em>.
                <p/><p/>
                To test a template, select it and click <strong>Test</strong>. You can then input UK numbers (omit the leading 0, e.g., 07475216398 → 7475216398) and optionally a first name.
                <p/><p/>
                After sending, confirm with the recipients that the message is correct, then mark “Test Successful?” to approve for campaigns. This sets its “Tested” status to <em>Tested</em>
                <br/><br/>
                <strong>Notes</strong>
                <p/><p/>
                onboarding_welcome_flow and utility_optout cannot be deleted — only edited.
                <p/><p/>
                Templates that are used by campaigns cannot be deleted.
                <p/><p/>
                Each approved WhatsApp template can only be used for one template created on this screen.
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Button variant="contained" onClick={openCreateDialog}>
                New Template
              </Button>
            </Stack>

            <WhatsAppTemplatesTable
              all_templates={allTemplates}
              delete_templates={deleteTemplates}
              test_template={openTestDialog}
              set_dialog_template={openEditDialog}
              isTemplateProtected={isTemplateProtected}
              protectedTemplateNames={[...PROTECTED_TEMPLATE_NAMES]}
            />
          </Container>
        </Box>
      )}

      <WhatsAppTemplatesDialog
        open={dialogState.is_dialog_open}
        type={dialogState.type}
        templateData={dialogState.template}
        onClose={closeDialog}
        onSave={handleSave}
        usedTemplateNameSet={usedTemplateNameSet}
      />

      <WhatsAppTestDialog
        open={testDialog.isOpen}
        template={testDialog.template}
        onClose={closeTestDialog}
        onSend={handleSendTest}
        onMarkSuccess={handleMarkSuccess}
      />
    </>
  );
});
